import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const NAV = [
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Security', to: '/security' },
  { label: 'PRD', to: '/prd' },
];

export default function MarketingLayout({ children }) {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#0A0A0A]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/immipro-logo.png" alt="ImmiPro" className="h-8 w-auto" />
            <div className="font-serif text-xl font-bold tracking-tight text-[#1A3A5C]">
              ImmiPro
            </div>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`text-sm font-medium transition-colors ${
                  loc.pathname === n.to ? 'text-[#2ECC9A]' : 'text-slate-700 hover:text-[#1A3A5C]'
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-[#1A3A5C]">
              Log in
            </Link>
            <Link to="/demo">
              <Button className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                Request demo
              </Button>
            </Link>
          </div>

          <button className="md:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="border-t border-slate-200 bg-white md:hidden">
            <div className="mx-auto max-w-7xl px-6 py-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-base font-medium text-slate-700"
                >
                  {n.label}
                </Link>
              ))}
              <div className="mt-3 flex flex-col gap-2">
                <Link to="/login" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full">Log in</Button>
                </Link>
                <Link to="/demo" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-[#1A3A5C] text-white">Request demo</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#1A3A5C]">
                <span className="font-serif text-sm font-bold text-[#2ECC9A]">i</span>
              </div>
              <div className="font-serif font-bold text-[#1A3A5C]">ImmiPro</div>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Your journey, guided with expertise.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Product</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/features" className="hover:text-[#1A3A5C]">Features</Link></li>
              <li><Link to="/pricing" className="hover:text-[#1A3A5C]">Pricing</Link></li>
              <li><Link to="/security" className="hover:text-[#1A3A5C]">Security</Link></li>
              <li><Link to="/prd" className="hover:text-[#1A3A5C]">Download PRD</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Compliance</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>OMARA Code aligned</li>
              <li>ISO 27001:2022</li>
              <li>Australian Privacy Principles</li>
              <li>NSW Law Society trust</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Get in touch</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li><Link to="/demo" className="hover:text-[#1A3A5C]">Book a demo</Link></li>
              <li>hello@immipro.com.au</li>
              <li>Sydney · Melbourne</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5">
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-6 text-xs text-slate-500 md:flex-row md:items-center">
            <div>© 2025 ImmiPro — Confidential, Commercial. Clean-room blueprint.</div>
            <div className="uppercase tracking-widest">Built compliance-first · designed client-first · priced practice-first</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
